import { useRef, useEffect } from "react";
import { C } from "../../theme/tokens";

/* ---------------------------------------------------------------
   AMBIENT BACKGROUND — canvas layer of slow-drifting sparkline
   curves (sage → gold, matching the Sparkline motif) plus a
   subtle SVG grain overlay on top. Both are fixed, full-viewport,
   pointer-events: none — purely decorative, never intercepts input.
------------------------------------------------------------------ */
export function AmbientBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf = null;
    let w = 0, h = 0, dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Each line: a handful of points that drift slowly right-to-left,
    // wrapping around, at slightly different speeds/amplitudes/hues
    // so the layers don't read as one repeating pattern.
    const LINES = [
      { y: 0.14, speed: 6,  amp: 26, points: 7, hue: 0,   width: 1.4, alpha: 0.10 },
      { y: 0.34, speed: 9,  amp: 34, points: 8, hue: 0.3, width: 1.1, alpha: 0.07 },
      { y: 0.58, speed: 4,  amp: 20, points: 6, hue: 0.6, width: 1.6, alpha: 0.08 },
      { y: 0.78, speed: 7.5,amp: 30, points: 9, hue: 0.8, width: 1.0, alpha: 0.06 },
      { y: 0.92, speed: 5,  amp: 18, points: 7, hue: 1,   width: 1.2, alpha: 0.05 },
    ];

    const SAGE = [91, 140, 255];   // C.greenBright
    const GOLD = [227, 184, 115];  // C.violetBright

    function lerpColor(t) {
      const r = SAGE[0] + (GOLD[0] - SAGE[0]) * t;
      const g = SAGE[1] + (GOLD[1] - SAGE[1]) * t;
      const b = SAGE[2] + (GOLD[2] - SAGE[2]) * t;
      return `${r | 0},${g | 0},${b | 0}`;
    }

    function resize() {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    let t0 = performance.now();

    function draw(now) {
      const elapsed = (now - t0) / 1000;
      ctx.clearRect(0, 0, w, h);

      for (const line of LINES) {
        const baseY = h * line.y;
        const step = w / (line.points - 1);
        const offset = (elapsed * line.speed) % step;

        ctx.beginPath();
        // draw one extra segment on each side so the drift never
        // leaves a visible gap at the edges as it scrolls
        for (let i = -1; i <= line.points; i++) {
          const x = i * step - offset;
          const wobble = Math.sin(elapsed * 0.4 + i * 1.3 + line.y * 10) * line.amp;
          const y = baseY + wobble;
          if (i === -1) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(${lerpColor(line.hue)},${line.alpha})`;
        ctx.lineWidth = line.width;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke();
      }

      raf = requestAnimationFrame(draw);
    }

    resize();
    raf = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: "-20%",
          width: "140%",
          height: "140%",
          zIndex: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(38% 32% at 22% 30%, ${C.greenBright}4A, transparent 70%),
            radial-gradient(34% 30% at 65% 62%, ${C.green}3C, transparent 70%)
          `,
          filter: "blur(70px)",
          animation: "gradientDrift 26s ease-in-out infinite",
          willChange: "transform",
        }}
      />
      <div
        style={{
          position: "fixed",
          inset: "-20%",
          width: "140%",
          height: "140%",
          zIndex: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(28% 26% at 78% 25%, ${C.violet}2A, transparent 70%),
            radial-gradient(24% 22% at 30% 75%, ${C.violetBright}20, transparent 70%)
          `,
          filter: "blur(65px)",
          animation: "gradientDrift2 19s ease-in-out infinite",
          willChange: "transform",
        }}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      {/* film-grain overlay — feTurbulence noise, very low opacity,
          screened on top so it reads as texture, not visible dots */}
      <svg
        style={{
          position: "fixed",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          pointerEvents: "none",
          opacity: 0.05,
          mixBlendMode: "overlay",
        }}
      >
        <filter id="grainFilter">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.85"
            numOctaves="2"
            stitchTiles="stitch"
          />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#grainFilter)" />
      </svg>
    </>
  );
}
